import { ConnectorFormDetails, Input, Label } from "@simplicity/ui";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";
import type { ConnectorFormProps } from "./GenericVarsForm";

type UpdateFn = (key: string, value: string | number) => void;

function TextField(props: {
  id: string;
  label: string;
  value: string;
  onUpdate: UpdateFn;
  fieldKey: string;
  placeholder: string;
}) {
  return (
    <div className="flex-1">
      <Label htmlFor={props.id} className="form-label">
        {props.label}
      </Label>
      <Input
        id={props.id}
        type="text"
        value={props.value}
        onChange={(e) => props.onUpdate(props.fieldKey, e.target.value)}
        placeholder={props.placeholder}
        className="font-mono"
      />
    </div>
  );
}

function NumberField(props: {
  id: string;
  label: string;
  value: number | "";
  onChange: (val: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex-1">
      <Label htmlFor={props.id} className="form-label">
        {props.label}
      </Label>
      <Input
        id={props.id}
        type="number"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="font-mono"
      />
    </div>
  );
}

function GcsFields(props: {
  gcsBucketOverride: string;
  gcsPrefixOverride: string;
  updateField: UpdateFn;
}) {
  return (
    <div className="flex gap-4">
      <TextField
        id="sm-gcs-bucket"
        label="GCS Bucket Override"
        value={props.gcsBucketOverride}
        onUpdate={props.updateField}
        fieldKey="gcsBucketOverride"
        placeholder="Optional — override derived bucket"
      />
      <TextField
        id="sm-gcs-prefix"
        label="GCS Prefix Override"
        value={props.gcsPrefixOverride}
        onUpdate={props.updateField}
        fieldKey="gcsPrefixOverride"
        placeholder="Optional — override derived prefix"
      />
    </div>
  );
}

function PollingFields(props: {
  pollIntervalMs: number | "";
  pollMaxAttempts: number | "";
  updateNumber: (key: string, raw: string) => void;
}) {
  return (
    <div className="flex gap-4">
      <NumberField
        id="sm-poll-interval"
        label="Poll Interval (ms)"
        value={props.pollIntervalMs}
        onChange={(v) => props.updateNumber("pollIntervalMs", v)}
        placeholder="Default: 30000"
      />
      <NumberField
        id="sm-poll-max-attempts"
        label="Poll Max Attempts"
        value={props.pollMaxAttempts}
        onChange={(v) => props.updateNumber("pollMaxAttempts", v)}
        placeholder="Default: 40"
      />
    </div>
  );
}

function DatasetFields(props: {
  bronzeDataset: string;
  silverDataset: string;
  connectorPrefix: string;
  updateField: UpdateFn;
}) {
  return (
    <div className="flex gap-4">
      <TextField
        id="sm-bronze-dataset"
        label="Bronze Dataset"
        value={props.bronzeDataset}
        onUpdate={props.updateField}
        fieldKey="bronzeDataset"
        placeholder={`Default: bronze__${props.connectorPrefix}`}
      />
      <TextField
        id="sm-silver-dataset"
        label="Silver Dataset"
        value={props.silverDataset}
        onUpdate={props.updateField}
        fieldKey="silverDataset"
        placeholder={`Default: silver__${props.connectorPrefix}`}
      />
    </div>
  );
}

function PrimaryFields(props: { str: (k: string) => string; updateField: UpdateFn }) {
  return (
    <>
      <div>
        <TextField
          id="sm-domain"
          label="Domain"
          value={props.str("domain")}
          onUpdate={props.updateField}
          fieldKey="domain"
          placeholder="e.g. superschools.schoolmint.net"
        />
      </div>
      <div>
        <TextField
          id="sm-account-email"
          label="Account Email"
          value={props.str("accountEmail")}
          onUpdate={props.updateField}
          fieldKey="accountEmail"
          placeholder="Super admin email"
        />
      </div>
      <div className="flex gap-4">
        <TextField
          id="sm-site-name"
          label="Site Name"
          value={props.str("siteName")}
          onUpdate={props.updateField}
          fieldKey="siteName"
          placeholder="Required"
        />
        <TextField
          id="sm-sftp-username"
          label="SFTP Username"
          value={props.str("sftpUsername")}
          onUpdate={props.updateField}
          fieldKey="sftpUsername"
          placeholder="Required"
        />
      </div>
    </>
  );
}

export function SchoolMintForm(props: ConnectorFormProps) {
  const tables = Array.isArray(props.vars.tables) ? (props.vars.tables as TableConfig[]) : [];
  const schedules = (
    props.vars.schedules != null &&
    typeof props.vars.schedules === "object" &&
    !Array.isArray(props.vars.schedules)
      ? props.vars.schedules
      : {}
  ) as Schedules;

  const str = (k: string) => (props.vars[k] as string) ?? "";
  const num = (k: string) => (typeof props.vars[k] === "number" ? props.vars[k] : "");

  function updateField(key: string, value: string | number) {
    const next = { ...props.vars };
    if (value === "" || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    props.onVarsChange(next);
    props.clearBanner();
  }

  function updateNumber(key: string, raw: string) {
    updateField(key, raw ? Number(raw) : "");
  }

  return (
    <>
      <PrimaryFields str={str} updateField={updateField} />
      <TablesEditor
        tables={tables}
        schedules={schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />
      {!props.hideAdvanced && (
        <ConnectorFormDetails
          summary="Advanced Options"
          className="mt-4 rounded border border-border/50"
        >
          <GcsFields
            gcsBucketOverride={str("gcsBucketOverride")}
            gcsPrefixOverride={str("gcsPrefixOverride")}
            updateField={updateField}
          />
          <PollingFields
            pollIntervalMs={num("pollIntervalMs")}
            pollMaxAttempts={num("pollMaxAttempts")}
            updateNumber={updateNumber}
          />
          <DatasetFields
            bronzeDataset={str("bronzeDataset")}
            silverDataset={str("silverDataset")}
            connectorPrefix={props.connectorPrefix}
            updateField={updateField}
          />
        </ConnectorFormDetails>
      )}
    </>
  );
}
